/* Reporte + CRUD: Usuarios ESE por Empresa Colaboradora (perfil Capacitador). */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  Building2, Users, Search, ChevronDown, ChevronRight,
  Download, RefreshCw, Mail, FileSpreadsheet,
  ChevronLeft, ChevronsLeft, ChevronsRight,
  Plus, Edit3, Trash2, KeyRound, X, UserPlus, Briefcase, AlertTriangle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 7;

const toTitleCase = (s) => s ? s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase()) : '';

const formatRut = (rut) => {
  if (!rut) return '—';
  const clean = rut.replace(/[.\-]/g, '');
  if (clean.length < 2) return rut;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv.toUpperCase()}`;
};

export default function CapacitadorUsuariosEmpresa() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(1);

  // Modales
  const [modalEmpresa, setModalEmpresa] = useState(false);
  const [modalUser, setModalUser] = useState(null);   // { mode:'create'|'edit', user?, empresaId? }
  const [modalReset, setModalReset] = useState(null); // { userId, nombre }
  const [modalDelEmpresa, setModalDelEmpresa] = useState(null); // { id, razon_social }

  async function cargar() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/capacitador/usuarios-por-empresa`, { headers });
      setData(r.data);
    } finally { setLoading(false); }
  }

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  function toggleExpand(id) { setExpanded(prev => ({ ...prev, [id]: !prev[id] })); }
  function expandirTodas() {
    if (!data) return;
    const all = {};
    data.empresas.forEach(e => { all[e.empresa_id] = true; });
    setExpanded(all);
  }
  function colapsarTodas() { setExpanded({}); }

  async function descargarExcel() {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/capacitador/usuarios-por-empresa/excel`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      a.download = `usuarios-ese-por-empresa_${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo generar el Excel.');
    } finally { setDownloading(false); }
  }

  async function eliminarUsuario(u, empresa) {
    if (!window.confirm(`¿Eliminar al usuario ESE "${u.nombre_completo}" de ${empresa.razon_social}?\n\nEsta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/capacitador/ese-usuarios/${u.id}`, { headers });
      toast.success('Usuario ESE eliminado');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar.');
    }
  }

  const filteredEmpresas = useMemo(() => {
    if (!data) return [];
    const q = (search || '').toLowerCase().trim();
    const qRut = q.replace(/[.\-]/g, '');
    if (!q) return data.empresas;
    return data.empresas
      .map(e => {
        if ((e.razon_social || '').toLowerCase().includes(q)) return e;
        const usuariosMatch = (e.usuarios || []).filter(u =>
          (u.nombre_completo || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q) ||
          (u.cargo || '').toLowerCase().includes(q) ||
          (u.rut || '').replace(/[.\-]/g, '').toLowerCase().includes(qRut)
        );
        if (usuariosMatch.length === 0) return null;
        return { ...e, usuarios: usuariosMatch, total_usuarios: usuariosMatch.length };
      })
      .filter(Boolean);
  }, [data, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredEmpresas.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedEmpresas = filteredEmpresas.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="px-6 py-6 max-w-6xl mx-auto" data-testid="usuarios-empresa-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-5">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-11 h-11 rounded-xl bg-teal-50 border border-teal-200 flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5 text-teal-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900">Usuarios ESE por Empresa</h1>
            <p className="text-sm text-slate-500">
              Cuentas de login (rol ESE / Gestión de Trabajadores) por Empresa Colaboradora.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-nowrap shrink-0">
          <Button variant="outline" size="sm" onClick={() => setModalEmpresa(true)}
            className="border-teal-300 text-teal-700 hover:bg-teal-50 whitespace-nowrap" data-testid="nueva-empresa-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nueva Empresa
          </Button>
          <Button size="sm" onClick={() => setModalUser({ mode: 'create' })}
            className="bg-teal-600 hover:bg-teal-700 text-white whitespace-nowrap" data-testid="nuevo-usuario-btn">
            <UserPlus className="w-4 h-4 mr-1.5" /> Nuevo Usuario ESE
          </Button>
          <Button onClick={descargarExcel} disabled={downloading || loading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap" size="sm" data-testid="exportar-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />
            {downloading ? 'Generando...' : 'Excel'}
          </Button>
          <Button variant="ghost" size="sm" onClick={cargar} className="whitespace-nowrap" data-testid="recargar-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Recargar
          </Button>
        </div>
      </div>

      {/* KPIs */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid="kpi-empresas">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Empresas</p>
            <p className="text-2xl font-black text-slate-900">{data.total_empresas}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid="kpi-usuarios">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Usuarios ESE</p>
            <p className="text-2xl font-black text-teal-700">{data.total_usuarios}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Con usuario ESE</p>
            <p className="text-2xl font-black text-slate-900">{data.empresas.filter(e => e.total_usuarios > 0).length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Sin usuario ESE</p>
            <p className="text-2xl font-black text-amber-600">{data.empresas.filter(e => e.total_usuarios === 0).length}</p>
          </div>
        </div>
      )}

      {/* Buscador */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar empresa, nombre, RUT, email o cargo..." className="pl-9"
            data-testid="search-input" />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={expandirTodas} data-testid="expandir-todas">Expandir todas</Button>
          <Button variant="outline" size="sm" onClick={colapsarTodas} data-testid="colapsar-todas">Colapsar todas</Button>
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <p className="py-14 text-center text-sm text-slate-400">Cargando reporte...</p>
        ) : !data || filteredEmpresas.length === 0 ? (
          <p className="py-14 text-center text-sm text-slate-400" data-testid="empty-state">
            {search ? 'Sin resultados para la búsqueda.' : 'No hay empresas registradas.'}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {paginatedEmpresas.map(e => {
              const open = !!expanded[e.empresa_id];
              return (
                <li key={e.empresa_id} data-testid={`empresa-${e.empresa_id}`}>
                  <div className="w-full flex items-center gap-2 px-5 py-3.5 hover:bg-slate-50 transition-colors">
                    <button type="button" onClick={() => toggleExpand(e.empresa_id)}
                      className="flex items-center gap-3 flex-1 text-left min-w-0"
                      data-testid={`toggle-empresa-${e.empresa_id}`}>
                      {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                      <Building2 className="w-4 h-4 text-teal-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{toTitleCase(e.razon_social)}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                        e.total_usuarios === 0
                          ? 'bg-slate-100 text-slate-500'
                          : 'bg-teal-50 text-teal-700 border border-teal-200'
                      }`}>
                        <Users className="w-3.5 h-3.5" />
                        {e.total_usuarios} {e.total_usuarios === 1 ? 'usuario' : 'usuarios'}
                      </span>
                    </button>
                    <Button variant="outline" size="sm"
                      onClick={() => setModalUser({ mode: 'create', empresaId: e.empresa_id })}
                      data-testid={`add-user-empresa-${e.empresa_id}`}>
                      <UserPlus className="w-3.5 h-3.5 mr-1" /> Agregar
                    </Button>
                    <button
                      onClick={() => setModalDelEmpresa({ id: e.empresa_id, razon_social: e.razon_social })}
                      className="inline-flex items-center p-2 rounded-lg hover:bg-rose-50 text-rose-600 border border-transparent hover:border-rose-200"
                      title="Eliminar empresa en cascada"
                      data-testid={`delete-empresa-${e.empresa_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {open && (
                    <div className="px-5 pb-4 pt-1 bg-slate-50/50 border-t border-slate-100" data-testid={`detalle-empresa-${e.empresa_id}`}>
                      {e.usuarios.length === 0 ? (
                        <p className="py-5 text-center text-xs text-slate-400 italic">— Sin usuarios ESE asignados —</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-[11px] uppercase tracking-wider text-slate-400">
                                <th className="text-left py-2 pr-3 font-semibold">RUT</th>
                                <th className="text-left py-2 pr-3 font-semibold">Nombre</th>
                                <th className="text-left py-2 pr-3 font-semibold">Cargo</th>
                                <th className="text-left py-2 pr-3 font-semibold">Email</th>
                                <th className="text-center py-2 pr-3 font-semibold">Estado</th>
                                <th className="text-right py-2 pr-2 font-semibold">Acciones</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {e.usuarios.map(u => (
                                <tr key={u.id} className="hover:bg-white" data-testid={`user-row-${u.id}`}>
                                  <td className="py-2 pr-3 font-mono text-xs text-slate-600">{formatRut(u.rut)}</td>
                                  <td className="py-2 pr-3 font-medium text-slate-900">{toTitleCase(u.nombre_completo)}</td>
                                  <td className="py-2 pr-3 text-slate-700">{toTitleCase(u.cargo) || '—'}</td>
                                  <td className="py-2 pr-3 text-slate-600">
                                    {u.email ? (
                                      <span className="inline-flex items-center gap-1 text-xs">
                                        <Mail className="w-3 h-3 text-slate-400" />{u.email}
                                      </span>
                                    ) : '—'}
                                  </td>
                                  <td className="py-2 pr-3 text-center">
                                    {u.activo ? (
                                      <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Activo</span>
                                    ) : (
                                      <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">Inactivo</span>
                                    )}
                                  </td>
                                  <td className="py-2 pr-2 text-right whitespace-nowrap">
                                    <button onClick={() => setModalUser({ mode: 'edit', user: u, empresaId: e.empresa_id })}
                                      className="inline-flex items-center p-1.5 rounded hover:bg-blue-50 text-blue-600 mr-1"
                                      title="Editar" data-testid={`edit-user-${u.id}`}>
                                      <Edit3 className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => setModalReset({ userId: u.id, nombre: u.nombre_completo })}
                                      className="inline-flex items-center p-1.5 rounded hover:bg-amber-50 text-amber-600 mr-1"
                                      title="Resetear clave" data-testid={`reset-user-${u.id}`}>
                                      <KeyRound className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => eliminarUsuario(u, e)}
                                      className="inline-flex items-center p-1.5 rounded hover:bg-rose-50 text-rose-600"
                                      title="Eliminar" data-testid={`delete-user-${u.id}`}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="text-[11px] text-slate-400 mt-4 text-center">
        <Download className="inline w-3 h-3 mr-1" />
        El Excel respeta los filtros del buscador desactivados (exporta el total de empresas y usuarios).
      </p>

      {/* Paginación */}
      {!loading && filteredEmpresas.length > 0 && (
        <div className="flex items-center justify-between mt-4 px-1" data-testid="pagination">
          <p className="text-xs text-slate-500">
            Mostrando <strong className="text-slate-700">{(currentPage - 1) * PAGE_SIZE + 1}</strong>
            {' – '}
            <strong className="text-slate-700">{Math.min(currentPage * PAGE_SIZE, filteredEmpresas.length)}</strong>
            {' de '}
            <strong className="text-slate-700">{filteredEmpresas.length}</strong> empresas
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setPage(1)} data-testid="pag-first" title="Primera"><ChevronsLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="pag-prev" title="Anterior"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="px-3 text-sm font-semibold text-slate-700 tabular-nums" data-testid="pag-info">{currentPage} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="pag-next" title="Siguiente"><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setPage(totalPages)} data-testid="pag-last" title="Última"><ChevronsRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* Modales */}
      {modalEmpresa && (
        <ModalEmpresa onClose={() => setModalEmpresa(false)} onCreated={() => { setModalEmpresa(false); cargar(); }} headers={headers} />
      )}
      {modalUser && (
        <ModalUsuarioEse modal={modalUser} onClose={() => setModalUser(null)} onSaved={() => { setModalUser(null); cargar(); }} headers={headers} empresasFromReport={data?.empresas || []} />
      )}
      {modalReset && (
        <ModalResetClave info={modalReset} onClose={() => setModalReset(null)} onDone={() => setModalReset(null)} headers={headers} />
      )}
      {modalDelEmpresa && (
        <ModalEliminarEmpresa info={modalDelEmpresa} onClose={() => setModalDelEmpresa(null)} onDeleted={() => { setModalDelEmpresa(null); cargar(); }} headers={headers} />
      )}
    </div>
  );
}


// ============================================================================
// Modales auxiliares
// ============================================================================
export function ModalEmpresa({ onClose, onCreated, headers }) {
  const [form, setForm] = useState({ razon_social: '', rut: '', acronimo: '', direccion: '', email_rrhh: '', telefono_rrhh: '' });
  const [saving, setSaving] = useState(false);

  async function submit(ev) {
    ev.preventDefault();
    if (!form.razon_social.trim() || !form.rut.trim()) {
      toast.error('Razón social y RUT son obligatorios.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/capacitador/empresas-colaboradoras`, form, { headers });
      toast.success(`Empresa "${form.razon_social}" creada.`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la empresa.');
    } finally { setSaving(false); }
  }

  return (
    <Backdrop onClose={onClose}>
      <ModalShell title="Nueva Empresa Colaboradora" icon={Building2} onClose={onClose} testId="modal-empresa">
        <form onSubmit={submit} className="space-y-3">
          <Field label="Razón Social *" required>
            <Input value={form.razon_social} onChange={e => setForm({ ...form, razon_social: e.target.value })} placeholder="Ej: Servicios Generales SpA" data-testid="empresa-razon-social" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="RUT *" required>
              <Input value={form.rut} onChange={e => setForm({ ...form, rut: e.target.value })} placeholder="77.123.456-K" data-testid="empresa-rut" />
            </Field>
            <Field label="Acrónimo"><Input value={form.acronimo} onChange={e => setForm({ ...form, acronimo: e.target.value })} placeholder="Ej: SG" /></Field>
          </div>
          <Field label="Dirección">
            <Input value={form.direccion} onChange={e => setForm({ ...form, direccion: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email RRHH"><Input value={form.email_rrhh} onChange={e => setForm({ ...form, email_rrhh: e.target.value })} /></Field>
            <Field label="Teléfono RRHH"><Input value={form.telefono_rrhh} onChange={e => setForm({ ...form, telefono_rrhh: e.target.value })} /></Field>
          </div>
          <ModalFooter saving={saving} onClose={onClose} testId="empresa-guardar" />
        </form>
      </ModalShell>
    </Backdrop>
  );
}

export function ModalUsuarioEse({ modal, onClose, onSaved, headers, empresasFromReport }) {
  const isEdit = modal.mode === 'edit';
  const u = modal.user;
  const [form, setForm] = useState({
    rut: u?.rut || '',
    nombre: u?.nombre || '',
    apellido: u?.apellido || '',
    email: u?.email || '',
    password: '',
    cargo: u?.cargo || '',
    empresa_colaboradora_id: modal.empresaId || '',
    activo: u?.activo !== false,
  });
  const [empresas, setEmpresas] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Si ya tenemos empresas en el reporte, las reutilizamos. Sino, las pedimos.
    if (empresasFromReport && empresasFromReport.length > 0) {
      setEmpresas(empresasFromReport.map(e => ({ id: e.empresa_id, razon_social: e.razon_social })));
    } else {
      axios.get(`${API}/capacitador/empresas-colaboradoras`, { headers })
        .then(r => setEmpresas(r.data))
        .catch(() => setEmpresas([]));
    }
  // eslint-disable-next-line
  }, []);

  async function submit(ev) {
    ev.preventDefault();
    if (!form.empresa_colaboradora_id) {
      toast.error('Selecciona una empresa.'); return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const body = {
          nombre: form.nombre, apellido: form.apellido,
          email: form.email, cargo: form.cargo,
          empresa_colaboradora_id: parseInt(form.empresa_colaboradora_id, 10),
          activo: !!form.activo,
        };
        await axios.put(`${API}/capacitador/ese-usuarios/${u.id}`, body, { headers });
        toast.success('Usuario ESE actualizado.');
      } else {
        if (!form.rut || !form.nombre || !form.email || !form.password) {
          toast.error('RUT, nombre, email y clave son obligatorios.'); setSaving(false); return;
        }
        await axios.post(`${API}/capacitador/ese-usuarios`, {
          ...form, empresa_colaboradora_id: parseInt(form.empresa_colaboradora_id, 10),
        }, { headers });
        toast.success('Usuario Contratista creado con Gestión de Trabajadores.');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar.');
    } finally { setSaving(false); }
  }

  return (
    <Backdrop onClose={onClose}>
      <ModalShell title={isEdit ? 'Editar Usuario ESE' : 'Nuevo Usuario ESE'} icon={UserPlus} onClose={onClose} testId="modal-usuario">
        <form onSubmit={submit} className="space-y-3">
          {!isEdit && (
            <div className="bg-teal-50/70 border border-teal-200 rounded-lg px-3 py-2 text-[11px] text-teal-800 leading-snug">
              <strong>Se creará como Usuario Contratista</strong> con la habilitación
              <em> Gestión de Trabajadores</em> activa. Podrá iniciar sesión, cargar a sus trabajadores y solicitarles capacitación.
            </div>
          )}
          <Field label="Empresa Colaboradora *" required>
            <select value={form.empresa_colaboradora_id}
              onChange={e => setForm({ ...form, empresa_colaboradora_id: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
              data-testid="usuario-empresa-select">
              <option value="">-- Seleccionar --</option>
              {empresas.map(emp => (
                <option key={emp.id} value={emp.id}>{toTitleCase(emp.razon_social)}</option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="RUT *" required>
              <Input value={form.rut} onChange={e => setForm({ ...form, rut: e.target.value })}
                placeholder="12.345.678-9" disabled={isEdit} data-testid="usuario-rut" />
            </Field>
            <Field label="Cargo">
              <Input value={form.cargo} onChange={e => setForm({ ...form, cargo: e.target.value })}
                placeholder="Jefe RRHH" data-testid="usuario-cargo" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nombre *" required>
              <Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} data-testid="usuario-nombre" />
            </Field>
            <Field label="Apellido">
              <Input value={form.apellido} onChange={e => setForm({ ...form, apellido: e.target.value })} data-testid="usuario-apellido" />
            </Field>
          </div>
          <Field label="Email *" required>
            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="usuario-email" />
          </Field>
          {!isEdit && (
            <Field label="Clave de acceso *" required>
              <Input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="Mínimo 4 caracteres" data-testid="usuario-password" />
            </Field>
          )}
          {isEdit && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} data-testid="usuario-activo" />
              <span className="text-slate-700">Usuario activo</span>
            </label>
          )}
          <ModalFooter saving={saving} onClose={onClose} testId="usuario-guardar" />
        </form>
      </ModalShell>
    </Backdrop>
  );
}

export function ModalResetClave({ info, onClose, onDone, headers }) {  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  async function submit(ev) {
    ev.preventDefault();
    if (password.length < 4) { toast.error('Mínimo 4 caracteres.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/capacitador/ese-usuarios/${info.userId}/reset-clave`, { password }, { headers });
      toast.success('Clave actualizada.');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cambiar la clave.');
    } finally { setSaving(false); }
  }
  return (
    <Backdrop onClose={onClose}>
      <ModalShell title={`Resetear clave · ${info.nombre}`} icon={KeyRound} onClose={onClose} testId="modal-reset">
        <form onSubmit={submit} className="space-y-3">
          <Field label="Nueva clave *" required>
            <Input type="text" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Mínimo 4 caracteres" autoFocus data-testid="reset-password-input" />
          </Field>
          <ModalFooter saving={saving} onClose={onClose} testId="reset-guardar" submitLabel="Guardar clave" />
        </form>
      </ModalShell>
    </Backdrop>
  );
}

export function ModalEliminarEmpresa({ info, onClose, onDeleted, headers }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    axios.get(`${API}/capacitador/empresas-colaboradoras/${info.id}/cascade-preview`, { headers })
      .then(r => setPreview(r.data))
      .catch(e => {
        toast.error(e?.response?.data?.detail || 'No se pudo cargar el preview.');
        onClose();
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, [info.id]);

  const expectedConfirm = (info.razon_social || '').trim().toUpperCase();
  const canDelete = confirmText.trim().toUpperCase() === expectedConfirm;

  async function ejecutarBorrado() {
    setDeleting(true);
    try {
      await axios.delete(`${API}/capacitador/empresas-colaboradoras/${info.id}`, { headers });
      toast.success(`Empresa "${info.razon_social}" eliminada.`);
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar.');
    } finally {
      setDeleting(false);
    }
  }

  const totales = preview?.totales || {};
  const ese = preview?.usuarios_ese || [];
  const plat = preview?.usuarios_plataforma || [];
  const totalUsuariosUnicos = new Set([
    ...ese.map(u => (u.email || '').toLowerCase()),
    ...plat.map(u => (u.email || '').toLowerCase()),
  ].filter(Boolean)).size;

  return (
    <Backdrop onClose={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8" data-testid="modal-eliminar-empresa">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-rose-100 bg-rose-50/60 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-600" />
            <h3 className="font-bold text-rose-900">Eliminar empresa en cascada</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-rose-100 rounded text-rose-500"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-700">
            Vas a eliminar <strong className="text-rose-700">{info.razon_social}</strong> y <strong>todos los datos vinculados</strong>.
            Esta acción <strong>no se puede deshacer</strong>.
          </p>

          {loading ? (
            <p className="py-6 text-center text-sm text-slate-400">Calculando datos a eliminar...</p>
          ) : preview && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                <CascadeBox label="Usuarios ESE (login)" count={totales.usuarios_ese} icon={UserPlus} color="rose" />
                <CascadeBox label="Usuarios contratista" count={totales.usuarios_plataforma} icon={Users} color="amber" />
                <CascadeBox label="Módulos asignados" count={totales.user_modules} icon={Briefcase} color="violet" />
                <CascadeBox label="Personas (trabajador)" count={totales.personas} icon={Users} color="blue" />
                <CascadeBox label="Vínculos empresa↔usuario" count={totales.links} icon={Building2} color="slate" />
                <CascadeBox label="La empresa misma" count={1} icon={Building2} color="rose" />
              </div>

              {totalUsuariosUnicos > 0 && (
                <details className="border border-slate-200 rounded-lg" data-testid="cascade-detalle">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 rounded-lg">
                    Ver los {totalUsuariosUnicos} usuarios que serán eliminados
                  </summary>
                  <div className="max-h-48 overflow-y-auto px-3 py-2 border-t border-slate-100 bg-slate-50/50">
                    <ul className="text-xs space-y-1">
                      {[...ese, ...plat].map((u, i) => (
                        <li key={`${u.email || u.rut}-${i}`} className="flex items-center gap-2 text-slate-700">
                          <span className="font-mono text-slate-400 w-28 shrink-0">{u.rut || '—'}</span>
                          <span className="flex-1 truncate">{toTitleCase((u.nombre || '').trim()) || '—'}</span>
                          <span className="text-slate-500 truncate">{u.email || ''}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-900 leading-snug">
                Para confirmar, escribe el nombre exacto de la empresa:
                <code className="block mt-1 font-mono font-bold text-amber-900 select-all break-all">{expectedConfirm}</code>
              </div>
              <Input
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="Escribe el nombre de la empresa para confirmar..."
                className={confirmText && !canDelete ? 'border-rose-300' : ''}
                data-testid="cascade-confirm-input"
                autoFocus
              />
            </>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="ghost" onClick={onClose} disabled={deleting}>Cancelar</Button>
            <Button
              type="button"
              onClick={ejecutarBorrado}
              disabled={!canDelete || deleting || loading}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40"
              data-testid="cascade-confirm-btn">
              <Trash2 className="w-4 h-4 mr-1.5" />
              {deleting ? 'Eliminando...' : 'Eliminar en cascada'}
            </Button>
          </div>
        </div>
      </div>
    </Backdrop>
  );
}

function CascadeBox({ label, count, icon: Icon, color }) {
  const colors = {
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    violet: 'bg-violet-50 border-violet-200 text-violet-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  }[color] || 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div className={`border rounded-lg px-3 py-2 ${colors}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="text-2xl font-black tabular-nums">{count ?? 0}</div>
    </div>
  );
}


function Backdrop({ children, onClose }) {  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {children}
    </div>
  );
}

function ModalShell({ title, icon: Icon, onClose, children, testId }) {
  return (
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-8" data-testid={testId}>
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-teal-600" />
          <h3 className="font-semibold text-slate-900">{title}</h3>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-400" aria-label="Cerrar">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function ModalFooter({ saving, onClose, testId, submitLabel = 'Guardar' }) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
      <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
      <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={testId}>
        <Briefcase className="w-4 h-4 mr-1.5" /> {saving ? 'Guardando...' : submitLabel}
      </Button>
    </div>
  );
}
